import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Reveal, SectionLabel } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENDERS = ["Female", "Trans-Female", "Male", "Non-Binary", "Other"];
const TYPES = ["Solo Model", "Couple / Duo"];

const Label = ({ children, required }) => (
  <label className="block text-[#cccccc] text-xs tracking-[0.15em] uppercase mb-2">
    {children}
    {required && <span className="text-[#d4af37]"> *</span>}
  </label>
);

const ErrText = ({ k, errors }) =>
  errors[k] ? (
    <p data-testid={`error-${k}`} className="text-[#e5484d] text-xs mt-1.5">
      {errors[k]}
    </p>
  ) : null;

const EMPTY = {
  first_name: "",
  last_name: "",
  age: "",
  location: "",
  email: "",
  gender: "",
  application_type: "",
  comfort_level: "",
  portfolio_links: "",
};

const ApplicationForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "First name is required";
    if (!form.age) e.age = "Age is required";
    else if (Number(form.age) < 18) e.age = "You must be 18 or older";
    if (!form.location.trim()) e.location = "Location is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.gender) e.gender = "Please select an option";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, {
        ...form,
        age: Number(form.age),
      });
      setDone(true);
      setForm(EMPTY);
      toast.success("Application submitted. We'll be in touch confidentially.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const err = (k) => (errors[k] ? "field-error" : "");

  return (
    <section
      id="apply"
      data-testid="apply-section"
      className="relative bg-[#0a0a0a] py-24 lg:py-32 border-t border-white/5"
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <SectionLabel number="04" title="Join Us" />
        <Reveal>
          <h2 className="font-display text-white font-extrabold tracking-tighter uppercase text-4xl sm:text-5xl lg:text-6xl leading-none mb-4">
            Talent Application
          </h2>
          <p className="text-[#cccccc] text-base sm:text-lg mb-12">
            If you are interested in collaborating, please reply with the
            following details:
          </p>
        </Reveal>

        {done ? (
          <motion.div
            data-testid="success-message"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-[#d4af37] bg-[#0d0d0d] p-12 text-center shadow-[inset_0_0_60px_rgba(212,175,55,0.06)]"
          >
            <CheckCircle2 className="h-12 w-12 text-[#d4af37] mx-auto mb-6" strokeWidth={1.4} />
            <h3 className="font-display text-white text-2xl font-bold uppercase tracking-tight mb-3">
              Application Received
            </h3>
            <p className="text-[#cccccc] mb-8">
              Thank you. Your submission is confidential and our casting team
              will review it shortly.
            </p>
            <button
              data-testid="submit-another-btn"
              onClick={() => setDone(false)}
              className="font-display text-sm font-bold tracking-[0.2em] uppercase text-[#d4af37] hover:text-white transition-colors duration-300"
            >
              Submit Another &rarr;
            </button>
          </motion.div>
        ) : (
          <Reveal delay={0.1}>
            <form
              onSubmit={handleSubmit}
              data-testid="application-form"
              noValidate
              className="space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label required>First Name</Label>
                  <input
                    data-testid="input-first_name"
                    className={`field-input ${err("first_name")}`}
                    value={form.first_name}
                    onChange={(e) => setField("first_name", e.target.value)}
                    placeholder="Jane"
                  />
                  <ErrText k="first_name" errors={errors} />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <input
                    data-testid="input-last_name"
                    className="field-input"
                    value={form.last_name}
                    onChange={(e) => setField("last_name", e.target.value)}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label required>Age</Label>
                  <input
                    data-testid="input-age"
                    type="number"
                    min="18"
                    className={`field-input ${err("age")}`}
                    value={form.age}
                    onChange={(e) => setField("age", e.target.value)}
                    placeholder="18+"
                  />
                  <ErrText k="age" errors={errors} />
                </div>
                <div>
                  <Label required>Location</Label>
                  <input
                    data-testid="input-location"
                    className={`field-input ${err("location")}`}
                    value={form.location}
                    onChange={(e) => setField("location", e.target.value)}
                    placeholder="City, Country"
                  />
                  <ErrText k="location" errors={errors} />
                </div>
              </div>

              <div>
                <Label required>Email</Label>
                <input
                  data-testid="input-email"
                  type="email"
                  className={`field-input ${err("email")}`}
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder="you@example.com"
                />
                <ErrText k="email" errors={errors} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label required>Gender</Label>
                  <select
                    data-testid="input-gender"
                    className={`field-input ${err("gender")}`}
                    value={form.gender}
                    onChange={(e) => setField("gender", e.target.value)}
                  >
                    <option value="" disabled>
                      Select...
                    </option>
                    {GENDERS.map((g) => (
                      <option key={g} value={g} className="bg-[#161616]">
                        {g}
                      </option>
                    ))}
                  </select>
                  <ErrText k="gender" errors={errors} />
                </div>
                <div>
                  <Label>Application Type</Label>
                  <select
                    data-testid="input-application_type"
                    className="field-input"
                    value={form.application_type}
                    onChange={(e) => setField("application_type", e.target.value)}
                  >
                    <option value="" disabled>
                      Select...
                    </option>
                    {TYPES.map((t) => (
                      <option key={t} value={t} className="bg-[#161616]">
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label>Background / On-Camera Comfort Level</Label>
                <textarea
                  data-testid="input-comfort_level"
                  rows={4}
                  className="field-input resize-none"
                  value={form.comfort_level}
                  onChange={(e) => setField("comfort_level", e.target.value)}
                  placeholder="A brief description of your background or comfort level on camera."
                />
              </div>

              <div>
                <Label>Portfolio / Social Links</Label>
                <input
                  data-testid="input-portfolio_links"
                  type="url"
                  className="field-input"
                  value={form.portfolio_links}
                  onChange={(e) => setField("portfolio_links", e.target.value)}
                  placeholder="Link to social media or previous work"
                />
              </div>

              <button
                type="submit"
                data-testid="submit-application-btn"
                disabled={submitting}
                className="w-full font-display font-bold text-sm tracking-[0.2em] uppercase bg-[#d4af37] text-black px-8 py-4 hover:bg-[#e5a93b] transition-all duration-300 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Application"
                )}
              </button>
              <p className="text-[#666] text-xs text-center">
                Your personal identity is kept entirely confidential.
              </p>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
};

export default ApplicationForm;
